import { ChangeDetectorRef, Directive, effect, inject, input, OnInit } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { Column } from '@miq/catalog';
import { MiqTouchable, MiqValidatable } from '@miq/shared-models';
import { isMasked } from '@miq/utils';
export interface FieldValidation {
  type: string;
  format: string;
  value: string;
}
@Directive()
export class BaseInput implements OnInit, MiqTouchable, MiqValidatable {
  readonly disabledInput = input(false, { alias: 'disabled' });
  disabled = false;
  readonly required = input(false);
  readonly currentValueInput = input<any>(undefined, { alias: 'currentValue' });
  currentValue: any;
  readonly UOMValidation = input(false);
  readonly warningInput = input(false, { alias: 'warning' });
  warning = false;
  readonly valueSetterInput = input<(data?: any) => any>(undefined, { alias: 'valueSetter' });
  readonly columnInput = input<Column>(undefined, { alias: 'column' });
  get column() {
    return this._column;
  }
  get valueSetter() {
    return this._valueSetter;
  }
  public fc: FormControl;
  public _valueSetter: (data?: any) => any;
  public maxlength: number;
  public lengthWarning: number;
  public validSnapshot = true;
  public isMaskedValue = false;
  protected _column: Column;
  protected cdr = inject(ChangeDetectorRef);
  constructor() {
    effect(() => {
      this.disabled = this.disabledInput();
      if (this.fc) {
        this.evaluateDisability();
        this.cdr.markForCheck();
      }
    });
    effect(() => {
      this.currentValue = this.currentValueInput();
      if (this.fc != null) {
        this.isMaskedValue = isMasked(this.currentValue);
        const isDisabled = this.disabled || this.isMaskedValue;
        if (isDisabled && this.fc.enabled) {
          this.fc.disable();
        } else if (!isDisabled && this.fc.disabled) {
          this.fc.enable();
        }
        this.cdr.markForCheck();
      }
    });
    effect(() => {
      this.warning = this.warningInput();
      this.cdr.markForCheck();
    });
    effect(() => {
      const v = this.valueSetterInput();
      if (v !== undefined) this._valueSetter = v;
    });
    effect(() => {
      const v = this.columnInput();
      if (v != null) {
        this._column = v;
        this._valueSetter = v.valueSetter;
      }
    });
    effect(() => {
      this.required();
      if (this.fc) {
        this.fc.setValidators(this.controlValidators());
        this.fc.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      }
    });
  }
  /**
   * Initializes isMaskedValue & fc attributes. form initialization is made
   * by calling initFormControl
   */
  ngOnInit(): void {
    // Sync signal values before init (effects run after lifecycle hooks)
    this.disabled = this.disabledInput();
    this.currentValue = this.currentValueInput();
    this.warning = this.warningInput();
    const vs = this.valueSetterInput();
    if (vs !== undefined) this._valueSetter = vs;
    const col = this.columnInput();
    if (col != null) {
      this._column = col;
      this._valueSetter = col.valueSetter;
    }
    this.isMaskedValue = isMasked(this.currentValue);
    this.initFormControl();
  }
  protected evaluateDisability() {
    this.disabled && this.fc ? this.fc.disable() : this.fc.enable();
  }
  protected updateValidators() {
    this.fc.setValidators(this.controlValidators());
  }
  /**
   * It creates a new FormControl with formState object returned by controlValues
   * method and validators array by calling controlValidators. this allows child
   * components to adjust the logic of form control creation as their own need.
   * if no change required this process works as default
   */
  protected initFormControl() {
    this.fc = new FormControl(this.controlValues(), this.controlValidators());
  }
  /**
   * Returns an object state for FormControl that defines the initial value and
   * the disabled(Optional) state of the control while using the
   * `new FormControl(formState, validatorOpts?, asyncValidator)`
   */
  protected controlValues(): { value: any; disabled?: boolean } {
    return { value: this.currentValue, disabled: this.disabled || this.isMaskedValue };
  }
  protected controlValidators(): ValidatorFn[] {
    const extraValidations = [];
    if (this.column && this.column.options && this.column.options.validations) {
      extraValidations.push(...this.setValidations(this.column.options.validations));
    }
    if (this.required()) {
      extraValidations.push(Validators.required);
    }
    return !this.isMaskedValue ? [...extraValidations] : [];
  }
  private setValidations(validationRaws: string[]): ValidatorFn[] {
    const result: ValidatorFn[] = [];
    validationRaws.forEach(validationRaw => {
      const [key, value] = validationRaw.split(':');
      const convertedNumber = Number(value);
      if (!isNaN(convertedNumber) && Number.isInteger(convertedNumber)) {
        if (key.includes('maxLength')) {
          this.maxlength = convertedNumber;
          result.push(Validators.maxLength(convertedNumber));
        } else if (key.includes('lengthWarning')) {
          this.lengthWarning = convertedNumber;
        }
      }
    });
    return result;
  }
  isValid(): boolean {
    if (isMasked(this.fc.value)) {
      return true;
    }
    const required = this.required();
    const valid =
      // form control is valid
      this.fc.valid ||
      // field NOT required AND either is untouched OR is disabled
      (!required && (this.fc.untouched || this.disabled)) ||
      // is disabled AND required AND value NOT empty
      (this.disabled && required && this.fc.value != null && this.fc.value !== '');
    if (!valid && (this.fc.pristine || this.fc.untouched)) {
      this.markAsTouched();
    }
    this.validSnapshot = valid;
    return valid;
  }
  isTouched() {
    return this.fc.dirty;
  }
  markAsTouched() {
    this.fc.markAsTouched();
  }
}
